import jsPDF from 'jspdf';
import { formatDate, sv, buildTruckReceiptData, FONT, layoutGeo as sharedLayoutGeo, layoutFieldHidden } from './helpers';

// ═══════════════════════════════════════════════════════════════════════════════
// TRUCK RECEIPT (NCC) / CONSIGNMENT NOTE PDF — data overlay for the pre-printed
// JAIN PARIVAHAN "NON CONSIGNEE COPY" (NCC) lorry receipt form (A4 landscape),
// the sheet headed "Head Office : 1, JADULAL MULLICK ROAD ... TEL.: 2259 3645".
// It is a different print run from the "NON-NEGOTIABLE COPY" (NNC) sheet, and
// its rules sit in different places, so the two generators cannot share numbers.
//
// Same print data and business logic as the Truck Receipt (NNC) generator in
// OtherDocumentsPDF.js — both read buildTruckReceiptData() and therefore share
// the saved truck-receipt record. Only the field positions differ, because the
// blanks sit elsewhere on the NCC stationery. Borders, headings and boxes are
// pre-printed, so each value must land exactly on its blank.
//
// Geometry (mm, A4 landscape 297 × 210), measured from a photographed NCC sheet
// that carries a known print: the values on it were laid down by the truck
// receipt overlay as it stood in commit cffd46e, so each piece of ink has an
// exact page coordinate. Fitting those ~20 known points gives a photo → page
// mapping, and the form's own rules were then read through it. Measuring ink
// rather than paper edges is what makes this reliable — an earlier attempt
// scaled the layout off a scan of the sheet and put every field several
// millimetres out, because a scan of a sheet is not the printed page.
//
//   Delivery office       three rules at y 58.0 / 64.0 / 70.3, box x 210 → 293
//   State / Tel No.       row y 70.3 → 79.4; labels end at x 223 / 256
//   Consignment note      box y 79.4 → 99.4, heading 82.6, "No." rule 90.5,
//                         "Date" rule 98.0. Both values are written above their
//                         rule, in the gap under the heading — see below.
//   From box              y 104.3 → 113.0, caption "From" above it at 102.5
//   To box                y 118.7 → 127.3, caption "To" above it at 118.2
//     NOTE: this form captions each box *above* it, so a value belongs in the
//     box under its label.
//   Insurance rules       y ≈ 88.0 (Company), 93.7 (Policy No./Date), 98.5
//                         (Amount/Risk); labels end at x 131.6 / 186
//   Consignor rules       y 115.4 (after the label, which ends at x 95) and one
//                         full-width continuation rule at 120.5
//   Consignee bank rules  y 130.3 (label ends at x 100.5) and one continuation
//                         rule at 135.6 — the table starts right below it
//   Goods table           top 138.2, header rule 147.5, floor 188.5
//     columns             41 | packages 61 | description 157.5 | actual 179.5 |
//                         charged 201.5 | rate 223.5 | amount → 293
//   Value rule            y 197.4, x 51 → 113
//
// Text is set to sit just above the rule it belongs to, the way the form is
// filled in by hand. The reference sheet had gone through the printer with a
// slight skew, which shows up as ~1 mm of drift between the left and right ends
// of a rule, so nothing here is tighter than about a millimetre — hence the
// margins built into the description column and the calibration sheet below.
//
// ── Aligning the overlay to your stationery ───────────────────────────────────
// Pre-printed forms vary between print runs, and printers feed them a millimetre
// or two off, so the overlay has to be calibrated once against the actual paper:
//
//   1. Print the calibration sheet (generateTruckReceiptNCCCalibrationPDF) onto a
//      blank pre-printed NCC form. Page 1 lays a 5 mm grid and a red baseline bar
//      over every field; page 2 is the key, printed on plain paper.
//   2. For each bar that does not sit on its blank, read the gap off the grid.
//   3. If every bar is off by the same amount, that is a feed offset — put the
//      correction in OX / OY below and nothing else changes. If only some bars
//      are off, correct those entries in GEO.
//
// GEO is the single source of truth: the real print and the calibration sheet
// both read it, so a mark on the sheet always shows where the value will land.
// ═══════════════════════════════════════════════════════════════════════════════

// Calibration offsets (mm), applied to every printed value. If the printer feeds
// the pre-printed form slightly off, shift everything at once: +X moves right,
// +Y moves down.
const OX = 0;
const OY = 0;

// Field geometry. `y` values are text baselines; `w` is the width the text is
// wrapped to, which is also the span drawn on the calibration sheet. Blocks that
// flow over several rules carry `first`/`rest`, and `sizes` is the font-size
// ladder they step down until the text fits its blanks.
const GEO = {
  // ── Insurance box (centre-left) ─────────────────────────────────────────
  insCompany:   { id: 'I1', label: 'Insurance company',        x: 135,   y: 88.0, w: 48 },
  insPolicyNo:  { id: 'I2', label: 'Policy No.',               x: 135,   y: 93.7, w: 38 },
  insPolicyDate:{ id: 'I3', label: 'Policy date',              x: 189,   y: 93.7, w: 28 },
  insAmount:    { id: 'I4', label: 'Insured amount',           x: 132,   y: 98.5, w: 41 },
  insRisk:      { id: 'I5', label: 'Risk',                     x: 189,   y: 98.5, w: 28 },

  // ── Address of delivery office (right column, three ruled lines) ────────
  delivery:     { id: 'D',  label: 'Address of delivery office', x: 212, w: 78,
                  ys: [56.8, 62.8, 69.1] },

  state:        { id: 'S1', label: 'State',                    x: 225,   y: 78.0, w: 28 },
  telNo:        { id: 'S2', label: 'Tel No.',                  x: 258,   y: 78.0, w: 30 },

  // ── Consignment note No. / Date (right column) ──────────────────────────
  // Both sit in the clear gap between the printed heading and their own rule,
  // which is where the reference copy carries them.
  noteNo:       { id: 'N1', label: 'Consignment note No.',     x: 225,   y: 88.0, w: 40 },
  noteDate:     { id: 'N2', label: 'Consignment date',         x: 225,   y: 95.6, w: 40 },

  // ── From / To boxes — one centred line, or two stacked when the place wraps
  from:         { id: 'F1', label: 'From (place)', x: 216, w: 68, y: 108.3, ys: [106.3, 110.5] },
  to:           { id: 'T1', label: 'To (place)',   x: 216, w: 68, y: 122.5, ys: [120.6, 124.8] },

  // ── Consignor / consignee-bank blocks (first line after the printed label,
  //    the rest on the full-width rule below). This form gives each block one
  //    continuation rule only — the consignor's runs out at the bank's printed
  //    label, the bank's at the goods table — so the type steps down until the
  //    text fits those two lines. Baselines sit 0.9 mm above their rule.
  consignor:    { id: 'C',  label: "Consignor's name & address",
                  first: { x: 99,  y: 114.5, w: 104 },
                  rest:  { x: 48,  w: 155, ys: [119.6] },
                  sizes: [6.5, 6, 5.5, 5] },
  bank:         { id: 'B',  label: "Consignee bank's name & address + NOTIFY",
                  first: { x: 103, y: 129.4, w: 100 },
                  rest:  { x: 48,  w: 155, ys: [134.7] },
                  sizes: [6.5, 6, 5.5, 5] },

  // ── Goods table ─────────────────────────────────────────────────────────
  packages:     { id: 'P1', label: 'Packages (printed bottom-to-top)', x: 52, y: 186, w: 40 },
  // The description column runs x 61 → 157.5 with the table floor at y 188.5.
  // Wrapping to 88 mm from x 66 leaves the longest line 3.5 mm clear of the
  // Weight column rule, enough to absorb the sheet skew that a hand-fed form
  // picks up; 12 rows keep the last baseline 1.8 mm above the floor.
  description:  { id: 'G',  label: 'Description / said to contain',
                  x: 66, y: 151.5, w: 88, lh: 3.2, rows: 12, sizes: [7.5, 7, 6.5, 6] },
  netWeight:    { id: 'W1', label: 'Total net weight (Actual column)',
                  cx: 168.5, labelY: 153, valueY: 157.5, labelW: 22, valueW: 21, lh: 2.2, vlh: 2.8 },
  grossWeight:  { id: 'W2', label: 'Total gross weight (Charged column)',
                  cx: 190.5, labelY: 164, valueY: 168.5, labelW: 22, valueW: 21, lh: 2.2, vlh: 2.8 },

  value:        { id: 'V1', label: 'Value', x: 56, y: 196.5, w: 40 },
};

// ── Saved layout profiles ─────────────────────────────────────────────────────
// The visual layout editor (TruckReceiptLayoutEditor) saves per-company
// profiles that adjust this geometry without code changes:
//   { ox, oy, fields: { <GEO key>: { dx, dy, hide } } }
// ox/oy shift every field at once (printer feed drift); dx/dy move one field;
// hide skips printing that field. The active profile is fetched by the caller
// and passed as the `layout` argument of both generators below.

export const TRUCK_RECEIPT_NCC_DOC_TYPE = 'truckReceiptNCC';

const layoutGeo = (layout) => sharedLayoutGeo(GEO, layout);
const fieldHidden = layoutFieldHidden;

// Field list for the visual layout editor: one draggable entry per GEO key,
// with the bounding box (page mm) the field's text occupies at zero offset.
export function getTruckReceiptNCCFields() {
  const ASC = 3.4; // a text baseline's box rises this far above it
  const line = (g) => ({ x: g.x, y: g.y - ASC, w: g.w, h: ASC + 1.2 });
  const lines = (g) => ({
    x: g.x, y: g.ys[0] - ASC, w: g.w,
    h: g.ys[g.ys.length - 1] - g.ys[0] + ASC + 1.2,
  });
  const flow = (g) => {
    const left = Math.min(g.first.x, g.rest.x);
    const right = Math.max(g.first.x + g.first.w, g.rest.x + g.rest.w);
    const bottom = g.rest.ys[g.rest.ys.length - 1];
    return { x: left, y: g.first.y - ASC, w: right - left, h: bottom - g.first.y + ASC + 1.2 };
  };
  const weight = (g) => ({
    x: g.cx - g.labelW / 2, y: g.labelY - 2.5, w: g.labelW,
    h: g.valueY + 3.5 - (g.labelY - 2.5),
  });
  const F = (key, box) => ({ key, id: GEO[key].id, label: GEO[key].label, box });
  return [
    F('insCompany', line(GEO.insCompany)),
    F('insPolicyNo', line(GEO.insPolicyNo)),
    F('insPolicyDate', line(GEO.insPolicyDate)),
    F('insAmount', line(GEO.insAmount)),
    F('insRisk', line(GEO.insRisk)),
    F('delivery', lines(GEO.delivery)),
    F('state', line(GEO.state)),
    F('telNo', line(GEO.telNo)),
    F('noteNo', line(GEO.noteNo)),
    F('noteDate', line(GEO.noteDate)),
    F('from', lines(GEO.from)),
    F('to', lines(GEO.to)),
    F('consignor', flow(GEO.consignor)),
    F('bank', flow(GEO.bank)),
    F('packages', { x: GEO.packages.x - 2, y: GEO.packages.y - GEO.packages.w, w: 5, h: GEO.packages.w }),
    F('description', {
      x: GEO.description.x, y: GEO.description.y - GEO.description.lh,
      w: GEO.description.w, h: GEO.description.rows * GEO.description.lh,
    }),
    F('netWeight', weight(GEO.netWeight)),
    F('grossWeight', weight(GEO.grossWeight)),
    F('value', line(GEO.value)),
  ];
}

export function generateTruckReceiptNCCPDF(document, company, authority, returnBlob = false, layout = null) {
  const doc = new jsPDF('l', 'mm', 'a4'); // form is landscape (297 × 210 mm)

  const data = buildTruckReceiptData(document, company);

  const EG = layoutGeo(layout);
  const show = (key) => !fieldHidden(layout, key);
  const LOX = OX + ((layout && layout.ox) || 0);
  const LOY = OY + ((layout && layout.oy) || 0);

  const put = (text, x, y, opts) => {
    const t = sv(text);
    if (t) doc.text(t, x + LOX, y + LOY, opts);
  };

  // Flow a long value onto its blank: the first line starts after the printed
  // label, the rest continue on the full-width ruled lines below. The NCC form
  // leaves less room than the NNC one, so the type size steps down until the
  // text fits instead of being cut off.
  const flowBlock = (text, { first, rest, sizes }) => {
    const t = sv(text);
    if (!t) return;
    for (let s = 0; s < sizes.length; s++) {
      doc.setFontSize(sizes[s]);
      const all = doc.splitTextToSize(t, first.w);
      const tail = all.length > 1
        ? doc.splitTextToSize(all.slice(1).join(' '), rest.w)
        : [];
      if (tail.length <= rest.ys.length || s === sizes.length - 1) {
        put(all[0], first.x, first.y);
        tail.slice(0, rest.ys.length).forEach((line, i) => put(line, rest.x, rest.ys[i]));
        return;
      }
    }
  };

  doc.setTextColor(0, 0, 0);
  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);

  // Demurrage days/rate are pre-printed on the form — nothing to overlay.

  // ── Insurance box (centre-left) ───────────────────────────────────────
  // No checkbox mark is printed (the reference copy leaves both boxes blank);
  // insurance details print only when the consignment is insured.
  if (data.insured) {
    doc.setFont(FONT, 'normal');
    doc.setFontSize(8);
    if (show('insCompany')) put(data.insurance_company, EG.insCompany.x, EG.insCompany.y);
    if (show('insPolicyNo')) put(data.policy_no, EG.insPolicyNo.x, EG.insPolicyNo.y);
    if (show('insPolicyDate')) put(formatDate(data.policy_date), EG.insPolicyDate.x, EG.insPolicyDate.y);
    if (show('insAmount')) put(data.insurance_amount, EG.insAmount.x, EG.insAmount.y);
    if (show('insRisk')) put(data.risk, EG.insRisk.x, EG.insRisk.y);
  }
  doc.setFont(FONT, 'normal');
  doc.setFontSize(8);

  // ── Address of delivery office (right column, three ruled lines) ──────
  if (show('delivery')) {
    const deliveryLines = doc.splitTextToSize(sv(data.delivery_office), EG.delivery.w);
    EG.delivery.ys.forEach((ly, i) => {
      if (deliveryLines[i]) put(deliveryLines[i], EG.delivery.x, ly);
    });
  }

  // ── State / Tel No. ───────────────────────────────────────────────────
  if (show('state')) put(data.state, EG.state.x, EG.state.y);
  if (show('telNo')) put(data.tel_no, EG.telNo.x, EG.telNo.y);

  // ── Consignment note No. / Date (right column) ────────────────────────
  doc.setFont(FONT, 'bold');
  doc.setFontSize(11);
  if (show('noteNo')) put(data.consignment_note_no, EG.noteNo.x, EG.noteNo.y); // "No. ______"
  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);
  if (show('noteDate')) put(formatDate(data.consignment_date), EG.noteDate.x, EG.noteDate.y); // "Date ______"

  // ── From / To boxes (right column, below the consignment note) ────────
  const placeBox = (text, g) => {
    const lines = doc.splitTextToSize(sv(text), g.w);
    if (lines.length <= 1) {
      put(lines[0], g.x, g.y);
    } else {
      put(lines[0], g.x, g.ys[0]);
      put(lines[1], g.x, g.ys[1]);
    }
  };
  if (show('from')) placeBox(data.from_place, EG.from);
  if (show('to')) placeBox(data.to_place, EG.to);

  // ── Consignor's Name & Address ────────────────────────────────────────
  // Full company block (name, address, e-mail, GSTIN) in small type: first
  // line after the label, the rest flowing onto the ruled lines below.
  if (show('consignor')) flowBlock(data.consignor, EG.consignor);

  // ── Consignee Bank's Name & Address + NOTIFY ──────────────────────────
  // "TO THE ORDER OF <bank>" followed by "NOTIFY: <buyer> AND <bank>",
  // flowing down into the space above the goods table.
  const bankText = [
    sv(data.consignee_bank),
    data.notify ? `NOTIFY: ${sv(data.notify)}` : '',
  ].filter(Boolean).join(' ');
  if (show('bank')) flowBlock(bankText, EG.bank);

  // ── Goods table body ──────────────────────────────────────────────────
  // Packages column — printed vertically (bottom-to-top) like the manual TRs
  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);
  if (show('packages')) put(sv(data.packages), EG.packages.x, EG.packages.y, { angle: 90 });

  // Description (said to contain)
  if (show('description')) {
    const g = EG.description;
    let descLines = [];
    for (let s = 0; s < g.sizes.length; s++) {
      doc.setFontSize(g.sizes[s]);
      descLines = doc.splitTextToSize(sv(data.description), g.w);
      if (descLines.length <= g.rows || s === g.sizes.length - 1) break;
    }
    descLines.slice(0, g.rows).forEach((line, i) => {
      put(line, g.x, g.y + i * g.lh);
    });
  }

  // Weight — net total in the "Actual" column, gross total in "Charged",
  // each labelled above the figure the way the reference copies are filled in
  const weightCell = (label, value, w) => {
    doc.setFont(FONT, 'bold');
    doc.setFontSize(5);   // keeps "TOTAL GROSS WEIGHT" on one line in its column
    doc.splitTextToSize(label, w.labelW).forEach((line, i) => {
      put(line, w.cx, w.labelY + i * w.lh, { align: 'center' });
    });
    doc.setFont(FONT, 'normal');
    doc.setFontSize(7);
    doc.splitTextToSize(sv(value), w.valueW).forEach((line, i) => {
      put(line, w.cx, w.valueY + i * w.vlh, { align: 'center' });
    });
  };
  if (show('netWeight')) weightCell('TOTAL NET WEIGHT', data.actual_weight, EG.netWeight);
  if (show('grossWeight')) weightCell('TOTAL GROSS WEIGHT', data.charged_weight, EG.grossWeight);

  // ── Value (bottom-left, on the pre-printed "Value" rule) ──────────────
  if (show('value')) {
    doc.setFont(FONT, 'bold');
    doc.setFontSize(10);
    if (doc.getTextWidth(sv(data.value)) > 38) doc.setFontSize(8);
    put(data.value, EG.value.x, EG.value.y);
  }

  if (returnBlob) {
    return doc.output('blob');
  }
  doc.save(`TruckReceiptNCC_${sv(document.invoice_no, document.id)}.pdf`);
}

// ═══════════════════════════════════════════════════════════════════════════════
// CALIBRATION SHEET — prints where the overlay lands, so it can be measured
// against the real stationery instead of guessed at from a photo.
//
// Page 1 (print onto a blank pre-printed NCC form): a 5 mm grid numbered every
// 10 mm, and a red bar on every field's baseline spanning the width that field
// wraps to. A bar sitting on its printed rule, ending inside its column, is
// aligned; anything else can be read straight off the grid.
//
// Page 2 (plain paper): the key — every mark's ID, what it is, and its current
// coordinates, so a correction can be applied to the right GEO entry.
// ═══════════════════════════════════════════════════════════════════════════════

const PAGE_W = 297;
const PAGE_H = 210;

function drawGrid(doc) {
  // Grid is paper-absolute — it represents the sheet, not the overlay, so the
  // OX/OY feed correction deliberately does not apply to it.
  for (let x = 0; x <= PAGE_W; x += 5) {
    const major = x % 25 === 0;
    doc.setDrawColor(major ? 155 : 215);
    doc.setLineWidth(major ? 0.12 : 0.05);
    doc.line(x, 0, x, PAGE_H);
  }
  for (let y = 0; y <= PAGE_H; y += 5) {
    const major = y % 25 === 0;
    doc.setDrawColor(major ? 155 : 215);
    doc.setLineWidth(major ? 0.12 : 0.05);
    doc.line(0, y, PAGE_W, y);
  }

  doc.setFont(FONT, 'normal');
  doc.setFontSize(4);
  doc.setTextColor(150);
  for (let x = 10; x < PAGE_W; x += 10) doc.text(String(x), x + 0.4, 3.2);
  for (let y = 10; y < PAGE_H; y += 10) doc.text(String(y), 0.6, y - 0.5);
}

export function generateTruckReceiptNCCCalibrationPDF(document, company, authority, returnBlob = false, layout = null) {
  const doc = new jsPDF('l', 'mm', 'a4');
  const legend = [];

  const EG = layoutGeo(layout);
  const hidden = (key) => fieldHidden(layout, key);
  const LOX = OX + ((layout && layout.ox) || 0);
  const LOY = OY + ((layout && layout.oy) || 0);

  drawGrid(doc);

  // A baseline bar with end ticks and an ID tag. `note` only reaches the key.
  // Hidden fields print their bar in grey so the sheet shows they exist but
  // will not be filled in.
  const mark = (id, label, x, y, w, note, isHidden) => {
    legend.push({
      id, label, x, y, w,
      note: isHidden ? [note, 'HIDDEN — will not print'].filter(Boolean).join('; ') : note,
    });
    const mx = x + LOX;
    const my = y + LOY;
    if (isHidden) doc.setDrawColor(175); else doc.setDrawColor(215, 25, 25);
    doc.setLineWidth(0.25);
    doc.line(mx, my, mx + w, my);
    doc.line(mx, my - 1.8, mx, my + 0.9);
    doc.line(mx + w, my - 1.3, mx + w, my + 0.7);
    doc.setFont(FONT, 'bold');
    doc.setFontSize(4.5);
    if (isHidden) doc.setTextColor(160); else doc.setTextColor(215, 25, 25);
    doc.text(id, mx + 0.4, my - 2.2);
  };

  // A secondary baseline (a wrapped continuation line) — same span, no tag.
  const faintMark = (x, y, w) => {
    doc.setDrawColor(240, 150, 150);
    doc.setLineWidth(0.2);
    doc.line(x + LOX, y + LOY, x + w + LOX, y + LOY);
  };

  // Insurance box — drawn whether or not this consignment is insured, so the
  // blanks can be aligned once and trusted later.
  ['insCompany', 'insPolicyNo', 'insPolicyDate', 'insAmount', 'insRisk']
    .forEach((k) => mark(EG[k].id, EG[k].label, EG[k].x, EG[k].y, EG[k].w, 'prints only when insured', hidden(k)));

  EG.delivery.ys.forEach((y, i) =>
    mark(`${EG.delivery.id}${i + 1}`, `${EG.delivery.label} — line ${i + 1}`,
      EG.delivery.x, y, EG.delivery.w, undefined, hidden('delivery')));

  ['state', 'telNo', 'noteNo', 'noteDate']
    .forEach((k) => mark(EG[k].id, EG[k].label, EG[k].x, EG[k].y, EG[k].w, undefined, hidden(k)));

  ['from', 'to'].forEach((k) => {
    const f = EG[k];
    mark(f.id, f.label, f.x, f.y, f.w, `wraps to y ${f.ys.join(' / ')}`, hidden(k));
    f.ys.forEach((y) => faintMark(f.x, y, f.w));
  });

  ['consignor', 'bank'].forEach((k) => {
    const b = EG[k];
    mark(`${b.id}1`, `${b.label} — first line (after the printed label)`,
      b.first.x, b.first.y, b.first.w, undefined, hidden(k));
    b.rest.ys.forEach((y, i) =>
      mark(`${b.id}${i + 2}`, `${b.label} — continuation ${i + 1}`, b.rest.x, y, b.rest.w, undefined, hidden(k)));
  });

  // Packages runs bottom-to-top, so its "baseline" is a vertical bar.
  {
    const p = EG.packages;
    const dim = hidden('packages');
    legend.push({
      id: p.id, label: p.label, x: p.x, y: p.y, w: p.w,
      note: dim ? 'rotated 90°, reads upward; HIDDEN — will not print' : 'rotated 90°, reads upward',
    });
    if (dim) doc.setDrawColor(175); else doc.setDrawColor(215, 25, 25);
    doc.setLineWidth(0.25);
    doc.line(p.x + LOX, p.y + LOY, p.x + LOX, p.y - p.w + LOY);
    doc.line(p.x - 0.9 + LOX, p.y + LOY, p.x + 1.8 + LOX, p.y + LOY);
    doc.setFont(FONT, 'bold');
    doc.setFontSize(4.5);
    if (dim) doc.setTextColor(160); else doc.setTextColor(215, 25, 25);
    doc.text(p.id, p.x + 2.2 + LOX, p.y - p.w + 1.5 + LOY);
  }

  // Description block — first and last row marked, with the full text box
  // outlined so overflow into the Actual column is obvious.
  {
    const d = EG.description;
    const lastY = d.y + (d.rows - 1) * d.lh;
    mark(`${d.id}1`, `${d.label} — row 1 of ${d.rows}`, d.x, d.y, d.w,
      `row pitch ${d.lh} mm, font ${d.sizes[0]}→${d.sizes[d.sizes.length - 1]} pt`, hidden('description'));
    mark(`${d.id}${d.rows}`, `${d.label} — row ${d.rows} (last)`, d.x, lastY, d.w, undefined, hidden('description'));
    for (let i = 1; i < d.rows - 1; i++) faintMark(d.x, d.y + i * d.lh, d.w);
    doc.setDrawColor(240, 150, 150);
    doc.setLineWidth(0.15);
    doc.rect(d.x + LOX, d.y - d.lh + LOY, d.w, d.rows * d.lh);
  }

  // Weight cells are centred on their column, so the bar is drawn centred too.
  ['netWeight', 'grossWeight'].forEach((k) => {
    const w = EG[k];
    mark(w.id, `${w.label} — label`, w.cx - w.labelW / 2, w.labelY, w.labelW,
      `centred on x ${w.cx}`, hidden(k));
    mark(`${w.id}v`, `${w.label} — figure`, w.cx - w.valueW / 2, w.valueY, w.valueW,
      `centred on x ${w.cx}`, hidden(k));
  });

  mark(EG.value.id, EG.value.label, EG.value.x, EG.value.y, EG.value.w, undefined, hidden('value'));

  // ── Page 2: the key ───────────────────────────────────────────────────
  doc.addPage('a4', 'l');
  doc.setTextColor(0);
  doc.setFont(FONT, 'bold');
  doc.setFontSize(13);
  doc.text('Truck Receipt (NCC) — calibration key', 12, 16);

  doc.setFont(FONT, 'normal');
  doc.setFontSize(8);
  [
    'Print page 1 onto a blank pre-printed NCC form. Each red bar is a text baseline; the bar spans the width that field wraps to.',
    'A field is aligned when its bar sits on the printed rule and ends inside its column. Read any gap off the 5 mm grid (numbered every 10 mm).',
    `If every bar is off by the same amount it is a printer feed offset — set the whole-page nudge in the Layout editor (currently X ${LOX}, Y ${LOY}).`,
    'If only some bars are off, drag those fields in the Layout editor (or type their gaps as per-field offsets) and save the profile.',
    'Pale bars are wrapped continuation lines of the field above them; the pale box around G1…G14 is the description text area.',
    'Grey bars are fields marked "don\'t print" in the active layout profile.',
  ].forEach((line, i) => doc.text(line, 12, 24 + i * 4.6));

  let y = 52;
  const col = { id: 12, label: 26, x: 150, yy: 166, w: 182, note: 200 };
  doc.setFont(FONT, 'bold');
  doc.setFontSize(8);
  doc.text('ID', col.id, y);
  doc.text('Field', col.label, y);
  doc.text('x', col.x, y);
  doc.text('y', col.yy, y);
  doc.text('width', col.w, y);
  doc.text('Notes', col.note, y);
  doc.setDrawColor(120);
  doc.setLineWidth(0.2);
  doc.line(12, y + 1.4, 285, y + 1.4);
  y += 5.4;

  doc.setFont(FONT, 'normal');
  doc.setFontSize(7);
  legend.forEach((f) => {
    if (y > PAGE_H - 12) {
      doc.addPage('a4', 'l');
      y = 20;
    }
    doc.setFont(FONT, 'bold');
    doc.text(f.id, col.id, y);
    doc.setFont(FONT, 'normal');
    doc.text(doc.splitTextToSize(f.label, 120)[0], col.label, y);
    doc.text(String(Math.round(f.x * 10) / 10), col.x, y);
    doc.text(String(Math.round(f.y * 10) / 10), col.yy, y);
    doc.text(String(Math.round(f.w * 10) / 10), col.w, y);
    if (f.note) doc.text(doc.splitTextToSize(f.note, 84)[0], col.note, y);
    y += 4.2;
  });

  if (returnBlob) {
    return doc.output('blob');
  }
  doc.save(`TruckReceiptNCC_Calibration_${sv(document.invoice_no, document.id)}.pdf`);
}

export default generateTruckReceiptNCCPDF;
